import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { roleLabel } from './theme';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, User, ChevronDown, ChevronRight, LogOut, HelpCircle, ArrowLeft, Pin, PinOff } from 'lucide-react';
import { Avatar } from '@/components/admin/Avatar';
import { AppHubButton } from '@/components/common/AppHubButton';
import { NotificationBell } from '@/components/welcome/NotificationBell';
import { TOTAL_E_SLOGAN } from '@/lib/brand';
import { MiamiHqFooter } from '@/components/common/MiamiHqFooter';
import { MobileNavGrid } from '@/components/common/mobile/MobileNavGrid';
import { getEntrance, postLogoutPath } from '@/lib/entrance';

const NavButton = ({ item, activeKey, onNavigate, wide, navAccent, child = false }) => {
  const Icon = item.icon;
  const active = item.key === activeKey;
  return (
    <button
      key={item.key}
      data-testid={`nav-${item.key}`}
      onClick={() => onNavigate(item.key)}
      className={`group relative flex w-full items-center rounded-xl transition-colors ${
        wide ? 'gap-3 px-4 min-h-[44px] xl:min-h-[50px] text-[16px] xl:text-[18px]' : 'gap-3 px-3 py-2.5 text-sm'
      } ${child ? (wide ? 'pl-9' : 'pl-8') : ''} ${
        active
          ? 'bg-white text-[#0E2A47] font-bold shadow-sm'
          : 'text-slate-300 hover:bg-white/10 hover:text-white'
      }`}
    >
      {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-1.5 rounded-r-full" style={{ backgroundColor: navAccent }} />}
      {Icon && <Icon size={wide ? 20 : (child ? 16 : 18)} className={active ? '' : 'text-slate-400 group-hover:text-white'} style={active ? { color: navAccent } : undefined} />}
      <span className="flex-1 text-left">{item.label}</span>
      {item.badge > 0 && (
        <span data-testid={`nav-badge-${item.key}`} className={`ml-auto rounded-full bg-[#C8641E] font-bold text-white ${wide ? 'px-2.5 py-0.5 text-sm' : 'px-1.5 py-0.5 text-[10px]'}`}>
          {item.badge}
        </span>
      )}
      {item.soon && (
        <span className="ml-auto text-[9px] uppercase tracking-wide text-slate-500">soon</span>
      )}
    </button>
  );
};

// Collapsible navigation group (optional). An item with `group: true` and `children`
// renders a header that expands/collapses its child entries. Backward compatible:
// plain items render exactly as before.
const NavGroup = ({ item, activeKey, onNavigate, wide, navAccent }) => {
  const Icon = item.icon;
  const childActive = (item.children || []).some((c) => c.key === activeKey);
  const [open, setOpen] = useState(childActive || !!item.defaultOpen);
  useEffect(() => { if (childActive) setOpen(true); }, [childActive]);
  return (
    <div data-testid={`nav-group-${item.key}`}>
      <button
        type="button"
        data-testid={`nav-group-toggle-${item.key}`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`group relative flex w-full items-center rounded-xl transition-colors ${
          wide ? 'gap-3 px-4 min-h-[44px] xl:min-h-[50px] text-[16px] xl:text-[18px]' : 'gap-3 px-3 py-2.5 text-sm'
        } ${childActive ? 'text-white font-semibold' : 'text-slate-300 hover:bg-white/10 hover:text-white'}`}
      >
        {Icon && <Icon size={wide ? 20 : 18} className={childActive ? '' : 'text-slate-400 group-hover:text-white'} style={childActive ? { color: navAccent } : undefined} />}
        <span className="flex-1 text-left uppercase tracking-wide text-[11px] font-bold">{item.label}</span>
        {(() => { const total = (item.children || []).reduce((s, c) => s + (c.badge || 0), 0); return total > 0 ? <span data-testid={`nav-group-badge-${item.key}`} className="mr-1 rounded-full bg-[#C8641E] px-1.5 py-0.5 text-[10px] font-bold text-white">{total}</span> : null; })()}
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>
      {open && (
        <div className={`mt-1 space-y-1 ${wide ? '' : ''}`} data-testid={`nav-group-children-${item.key}`}>
          {(item.children || []).map((c) => (
            <NavButton key={c.key} item={c} activeKey={activeKey} onNavigate={onNavigate} wide={wide} navAccent={navAccent} child />
          ))}
        </div>
      )}
    </div>
  );
};

const SidebarInner = ({ navItems, activeKey, onNavigate, brandTitle, brandSubtitle, brandNode, user, wide = false, clockMode = 'miami', bottomSlot = null, navAccent = '#8FC93A', pinControl = null }) => (
  <div className="flex h-full flex-col bg-[#0E2A47] text-slate-200">
    {/* Pin / unpin control — top of the navigation menu */}
    {pinControl}
    {/* Brand */}
    {brandNode ? brandNode : (
      <div className="flex flex-col items-center border-b border-white/10 px-4 pt-6 pb-5 text-center">
        <img src="/rout_logo.png" alt="ROUT by TFS" className="w-[70%] max-w-[150px] h-auto" style={{ aspectRatio: '1 / 1' }} />
        <div className="mt-3 text-base font-semibold text-white tracking-tight">Your Facility. On ROUT.</div>
      </div>
    )}

    {/* Nav */}
    <nav className={`flex-1 overflow-y-auto ${wide ? 'px-4 py-6 space-y-2 pb-24' : 'px-3 py-4 space-y-1 pb-20'}`}>
      {navItems.map((item) => (
        item.group
          ? <NavGroup key={item.key} item={item} activeKey={activeKey} onNavigate={onNavigate} wide={wide} navAccent={navAccent} />
          : <NavButton key={item.key} item={item} activeKey={activeKey} onNavigate={onNavigate} wide={wide} navAccent={navAccent} />
      ))}
    </nav>

    <div className={`mt-auto border-t border-white/10 ${wide ? 'px-3 py-3' : 'px-2.5 py-3'} space-y-2`}>
      {bottomSlot}
      <MiamiHqFooter inline />
    </div>
  </div>
);

// Phones, narrow windows and tablets (incl. landscape, up to 1365px) get the collapsible
// overlay sidebar. Wide displays (>=1366px) — including touchscreen laptops and large
// touch monitors — keep the approved permanently docked sidebar regardless of pointer type.
const COMPACT_QUERY = '(max-width: 1023px), (max-width: 1365px) and (pointer: coarse)';
const matchesCompact = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(COMPACT_QUERY).matches;

const useCompactLayout = () => {
  const [compact, setCompact] = useState(matchesCompact);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(COMPACT_QUERY);
    const onChange = () => setCompact(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange); else mq.addListener(onChange);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
  }, []);
  return compact;
};

const SidebarShell = ({
  navItems, activeKey, onNavigate,
  brandTitle = 'TFS OPERATIONS', brandSubtitle = 'Work Order Mgmt', brandNode = null,
  workspaceLabel = 'Workspace', logoutTo = '/login', showPortalHome = true,
  wide = false, clockMode = 'miami', mobileBrand = null, children, bottomSlot = null,
  backAction = null, mobileNavGrid = false, mobileNavAccent = '#0E2A47', navAccent = '#8FC93A',
}) => {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const compact = useCompactLayout();

  // User-controlled collapsible navigation. Pinned = docked open (default);
  // unpinned = collapsed so content expands, reopen via the hamburger. Preference
  // persists per user across navigation and sessions.
  const pinKey = `tfs_nav_pinned_${user?.id || 'anon'}`;
  const [pinned, setPinned] = useState(() => {
    if (typeof window === 'undefined') return true;
    const v = window.localStorage.getItem(pinKey);
    return v === null ? true : v === '1';
  });
  useEffect(() => {
    const v = window.localStorage.getItem(pinKey);
    setPinned(v === null ? true : v === '1');
  }, [pinKey]);
  const persistPin = (val) => {
    setPinned(val);
    try { window.localStorage.setItem(pinKey, val ? '1' : '0'); } catch { /* ignore */ }
  };

  const handleLogout = () => { logout(); window.location.assign(getEntrance() ? postLogoutPath() : logoutTo); };
  const handleNav = (key) => { onNavigate(key); setMobileOpen(false); };

  // Docked sidebar shows only on wide screens AND when pinned.
  const docked = !compact && pinned;
  // Hamburger (overlay) is available on compact screens, or on desktop when unpinned.
  const showHamburger = compact || !pinned;

  const pinControl = (collapseAction) => (
    <div className="flex items-center justify-between border-b border-white/10 px-3 py-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Menu</span>
      <button
        data-testid="nav-pin-toggle"
        onClick={collapseAction}
        title={pinned ? 'Unpin menu (collapse)' : 'Pin menu open'}
        aria-label={pinned ? 'Unpin menu' : 'Pin menu open'}
        className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-300 hover:bg-white/10 hover:text-white"
      >
        {pinned ? <PinOff size={15} /> : <Pin size={15} />}
        <span className="hidden sm:inline">{pinned ? 'Unpin' : 'Pin'}</span>
      </button>
    </div>
  );

  const asideW = wide ? 'lg:w-72 xl:w-[288px] 2xl:w-[320px]' : 'w-64';
  const mainPad = wide ? 'lg:pl-72 xl:pl-[288px] 2xl:pl-[320px]' : 'lg:pl-64';
  const sheetW = wide ? 'w-[300px]' : 'w-64';

  return (
    <div className="min-h-screen bg-[#F5F6F8]">
      {/* Desktop sidebar (wide mouse/trackpad screens only, and only when pinned) */}
      {docked && (
        <aside className={`flex fixed inset-y-0 left-0 ${asideW} z-40`} data-testid="sidebar-desktop">
          <SidebarInner navItems={navItems} activeKey={activeKey} onNavigate={handleNav}
            brandTitle={brandTitle} brandSubtitle={brandSubtitle} brandNode={brandNode} user={user}
            wide={wide} clockMode={clockMode} bottomSlot={bottomSlot} navAccent={navAccent}
            pinControl={pinControl(() => persistPin(false))} />
        </aside>
      )}

      <div className={docked ? mainPad : ''}>
        {/* Universal Total E header (single shared implementation across all hubs) */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 bg-[#101418] border-b border-white/10 px-4 lg:px-8 py-2.5 text-white" data-testid="universal-header">
          <div className="flex min-w-0 items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className={`${showHamburger ? '' : 'hidden'} text-white`} data-testid="sidebar-mobile-toggle" aria-label="Open menu"><Menu size={24} /></button>
              </SheetTrigger>
              <SheetContent side="left" className={`p-0 ${sheetW} border-0`}>
                <SidebarInner navItems={navItems} activeKey={activeKey} onNavigate={handleNav}
                  brandTitle={brandTitle} brandSubtitle={brandSubtitle} brandNode={brandNode} user={user}
                  wide={wide} clockMode={clockMode} bottomSlot={bottomSlot} navAccent={navAccent}
                  pinControl={!compact ? pinControl(() => { persistPin(true); setMobileOpen(false); }) : null} />
              </SheetContent>
            </Sheet>
            <img src="/total_e_icon.webp" alt="Total-E" data-testid="universal-header-logo"
              className={`shrink-0 rounded-lg object-contain ${compact ? 'h-8 w-8' : 'h-10 w-10'}`} />
            <div className="min-w-0 leading-tight">
              <span className={`block truncate font-bold uppercase tracking-[0.18em] text-slate-200 ${compact ? 'text-xs' : (wide ? 'text-sm' : 'text-[13px]')}`} data-testid="universal-header-workspace">{workspaceLabel}</span>
              {!compact && (
                <>
                  <p className="truncate text-[12px] font-medium text-slate-300" data-testid="universal-header-slogan">{TOTAL_E_SLOGAN}</p>
                  <p className="truncate text-[11px] text-slate-400">Signed in as <span className="font-semibold text-white" data-testid="universal-header-role">{roleLabel(user?.role)}</span></p>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {backAction && (
              <button onClick={backAction} data-testid="topbar-back-btn"
                className={`hidden sm:flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-slate-100 font-medium hover:bg-white/10 ${wide ? 'text-base' : 'text-sm'}`}>
                <ArrowLeft size={15} /> Back
              </button>
            )}
            <NotificationBell token={token} />
            {showPortalHome && <AppHubButton variant="dark" />}
            <button className={`hidden md:flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-slate-200 hover:bg-white/10 ${wide ? 'text-base' : 'text-sm'}`} data-testid="help-btn">
              <HelpCircle size={15} /> Help
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-2 py-1 hover:bg-white/10" data-testid="user-menu-btn">
                  <Avatar userId={user?.id} hasPhoto={!!user?.photo_url} name={user?.name} size={wide ? 32 : 28} />
                  <span className={`hidden sm:inline font-medium text-white ${wide ? 'text-base' : 'text-sm'}`}>{user?.name}</span>
                  <ChevronDown size={14} className="text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium">{user?.name}</span>
                    <span className="text-xs text-gray-500">{user?.email}</span>
                    <span className="text-xs text-[#5FA718] mt-1 font-semibold">Signed in as {roleLabel(user?.role)}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {backAction && (
                  <DropdownMenuItem onClick={backAction} className="sm:hidden" data-testid="menu-back-btn">
                    <ArrowLeft size={15} className="mr-2" /> Back
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="my-profile-btn">
                  <User size={15} className="mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut size={15} className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className={`mx-auto max-w-[1600px] rout-noxscroll ${wide ? 'px-4 lg:px-10 py-8' : 'px-4 lg:px-8 py-6'}`}>
          {/* Mobile-only persistent section navigation — rendered on EVERY internal
              screen (not just Overview) with the current section highlighted, so the
              hamburger is never required. Non-sticky: it sits at the top and scrolls
              away. Desktop keeps its docked sidebar. */}
          {mobileNavGrid && compact && navItems.length > 0 && (
            <section className="mb-5" data-testid="shell-mobile-nav-wrap">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{workspaceLabel}</div>
              <MobileNavGrid
                accent={mobileNavAccent}
                columns={3}
                testid="shell-mobile-nav"
                items={navItems.flatMap((it) => (it.group ? (it.children || []) : [it])).map((it) => ({
                  key: it.key, label: it.label, icon: it.icon, badge: it.badge, soon: it.soon,
                  active: it.key === activeKey, onClick: () => handleNav(it.key),
                }))}
              />
            </section>
          )}
          {children}
        </main>
      </div>
    </div>
  );
};

export default SidebarShell;
